// src/reducers/mainPageReducer.ts
import {ILanguageConfiguration} from "../models/interfaces/languageConfiguration.interface.ts";
import {billedMinutesForFile} from "../utils/creditsCost.ts";
import {LANGUAGE_DEFAULT_CONFIGURATION} from "../models/constants/constants.ts";
import {SanitizedPreset} from "../utils/presetSanitizer.ts";


export interface ISubtitleState {
  sourceLanguage: Record<string, ILanguageConfiguration>;
  isTranscribeOnly?: boolean;
  targetLanguages: Record<string, ILanguageConfiguration>;
  shotChangeSync?: boolean;
  dubbingScript?: boolean;
  keepSongs?: boolean;
  /** True once the user toggled Keep songs; a workspace default seed never overrides a touched value. */
  keepSongsTouched?: boolean;
}

export interface ICreateNewProjectState {
  attachedFiles: File[];
  customConfigurationId?: string;
  customConfigurationName: string;
  shouldSaveTemplate: boolean;
  fileNameToSeriesNameRelation: Record<string, string>;
  durationSecondsByFileName: Record<string, number>;
  subtitles: ISubtitleState;
  errors: string[];
}

export type CreateNewProjectAction =
  | { type: "SET_FILES"; payload: File[] }
  | { type: "SET_FILE_SERIES_RELATION"; payload: Record<string, string> }
  | { type: "SET_CUSTOM_CONFIGURATION_ID"; payload: string | undefined }
  | { type: "SET_CUSTOM_CONFIGURATION_NAME"; payload: string }
  | { type: "SET_SHOULD_SAVE_TEMPLATE"; payload: boolean }
  | { type: "SET_FILE_DURATION"; payload: { fileName: string; seconds: number } }
  | { type: "RENAME_FILE_DURATION"; payload: { from: string; to: string } }
  | { type: "REMOVE_FILE_DURATION"; payload: { fileName: string } }
  | { type: "SET_SOURCE_LANGUAGE"; payload: Record<string, ILanguageConfiguration> }
  | { type: "SET_TARGET_LANGUAGES"; payload: Record<string, ILanguageConfiguration> }
  | { type: "MARK_SUBTITLE_SETTINGS_DIRTY" }
  | { type: "TOGGLE_TRANSCRIBE_ONLY"; }
  | { type: "TOGGLE_SHOT_CHANGE_SYNC"; }
  | { type: "TOGGLE_DUBBING_SCRIPT"; }
  | { type: "TOGGLE_KEEP_SONGS"; }
  | { type: "SET_KEEP_SONGS"; payload: boolean }
  | { type: "SET_ERRORS"; payload: string[] }
  | { type: "APPLY_PRESET"; payload: SanitizedPreset }


export const initialCreateNewProjectState: ICreateNewProjectState = {
  attachedFiles: [] as File[],
  customConfigurationId: undefined,
  customConfigurationName: "",
  shouldSaveTemplate: false,
  fileNameToSeriesNameRelation: {} as Record<string, string>,
  durationSecondsByFileName: {} as Record<string, number>,
  subtitles: {
    sourceLanguage: {} as Record<string, ILanguageConfiguration>,
    targetLanguages: {} as Record<string, ILanguageConfiguration>,
    isTranscribeOnly: false,
    shotChangeSync: false,
    dubbingScript: false,
    keepSongs: false,
    keepSongsTouched: false,
  } as ISubtitleState,
  errors: [],
};

export const getTotalBilledMinutes = (state: ICreateNewProjectState): number =>
  Object.values(state.durationSecondsByFileName).reduce(
    (sum: number, seconds: number) => sum + billedMinutesForFile(seconds),
    0,
  );

export const getTotalRawSeconds = (state: ICreateNewProjectState): number =>
  Object.values(state.durationSecondsByFileName).reduce(
    (sum: number, seconds: number) => sum + seconds,
    0,
  );

export function newProjectReducer(
  state: ICreateNewProjectState,
  action: CreateNewProjectAction
): ICreateNewProjectState {
  switch (action.type) {
    case "SET_FILES":
      return {...state, attachedFiles: action.payload};
    case "SET_FILE_SERIES_RELATION":
      return {...state, fileNameToSeriesNameRelation: action.payload};
    case "SET_CUSTOM_CONFIGURATION_ID":
      return {...state, customConfigurationId: action.payload};
    case "SET_CUSTOM_CONFIGURATION_NAME":
      return {...state, customConfigurationName: action.payload};
    case "SET_SHOULD_SAVE_TEMPLATE":
      return {...state, shouldSaveTemplate: action.payload};
    case "SET_FILE_DURATION":
      return {
        ...state,
        durationSecondsByFileName: {
          ...state.durationSecondsByFileName,
          [action.payload.fileName]: action.payload.seconds,
        },
      };
    case "RENAME_FILE_DURATION": {
      const {from, to} = action.payload;
      const seconds: number | undefined = state.durationSecondsByFileName[from];
      if (seconds === undefined || from === to) return state;

      const renamedDurations: Record<string, number> = {
        ...state.durationSecondsByFileName,
        [to]: seconds,
      };
      delete renamedDurations[from];
      return {...state, durationSecondsByFileName: renamedDurations};
    }
    case "REMOVE_FILE_DURATION": {
      if (!(action.payload.fileName in state.durationSecondsByFileName)) return state;

      const remainingDurations: Record<string, number> = {...state.durationSecondsByFileName};
      delete remainingDurations[action.payload.fileName];
      return {...state, durationSecondsByFileName: remainingDurations};
    }
    case "SET_SOURCE_LANGUAGE":
      return {
        ...state,
        subtitles: {...state.subtitles, sourceLanguage: action.payload}
      };
    case "SET_TARGET_LANGUAGES":
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          targetLanguages: action.payload,
        }
      };
    case "TOGGLE_TRANSCRIBE_ONLY":
      // Do not clear targetLanguages here: the payload already emits `subtitles: {}`
      // while transcribe-only is on (useProjectUploader.ts:38-39).
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          isTranscribeOnly: !state.subtitles.isTranscribeOnly
        }
      };
    case "TOGGLE_SHOT_CHANGE_SYNC":
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          shotChangeSync: !state.subtitles.shotChangeSync
        }
      };
    case "TOGGLE_DUBBING_SCRIPT":
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          dubbingScript: !state.subtitles.dubbingScript
        }
      };
    case "TOGGLE_KEEP_SONGS":
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          keepSongs: !state.subtitles.keepSongs,
          keepSongsTouched: true,
        }
      };
    case "SET_KEEP_SONGS":
      // Workspace-default seed on source selection: sets the value without claiming a user decision.
      return {
        ...state,
        subtitles: {
          ...state.subtitles,
          keepSongs: action.payload,
        }
      };
    case "MARK_SUBTITLE_SETTINGS_DIRTY":
      return {...state, shouldSaveTemplate: true};
    case "SET_ERRORS":
      return {
        ...state,
        errors: action.payload
      };
    case "APPLY_PRESET": {
      const {sourceLanguageName, sourceConfig, targetsByName, shotChangeSync, dubbingScript, keepSongs, isTranscribeOnly} = action.payload;
      return {
        ...state,
        customConfigurationName: "",
        shouldSaveTemplate: false,
        subtitles: {
          ...state.subtitles,
          keepSongs: keepSongs === undefined ? state.subtitles.keepSongs : keepSongs,
          // A preset whose source is unsupported must not wipe a source the user already picked.
          sourceLanguage: sourceLanguageName === null
            ? state.subtitles.sourceLanguage
            : {[sourceLanguageName]: sourceConfig ?? LANGUAGE_DEFAULT_CONFIGURATION},
          targetLanguages: targetsByName,
          isTranscribeOnly,
          shotChangeSync,
          dubbingScript,
        }
      };
    }
    default:
      return state;

  }
}
